/**
 * @author taulmax
 * @date 2024.10.07
 * @description 가맹점별 판매 통계 페이지
 */
import SalesStatisticsFilter from './filter/SalesStatisticsFilter';
import FranchiseRankingTable from './table/FranchiseRankingTable';
import DailySalesTable from './table/DailySalesTable';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useSalesStatisticsStore,
  IDailySalesDetail,
} from '../../../store/useSalesStatistics';
import apiClient from '../../../service/apiClient';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import queryString from 'query-string';
import { formatDate, updateUrlWithParams, getStartDateByPeriod, PeriodType } from '../../../util/util';
import { useLoginStore } from '../../../store/useLogin';
import { useFranchiseStore } from '../../../store/useFranchise';

const DEFAULT_DAILY_PAGE_SIZE = 20;

const Statistics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('month');
  const [activeTable, setActiveTable] = useState<'ranking' | 'daily'>('ranking');
  const [rankingPage, setRankingPage] = useState(0);
  const [rankingPageSize, setRankingPageSize] = useState(20);

  const { role } = useLoginStore();
  const isAdmin = role === 'ROLE_ADMIN';
  const { getFranchise } = useFranchiseStore();

  const {
    getSalesStatistics,
    getSalesStatisticsLoading,
    franchiseSummary,
    getDailySales,
    dailySales,
    dailySalesLoading,
    dailySalesPage,
    dailySalesTotalSummary,
    getDailySalesDetail,
    dailySalesDetails,
    dailySalesDetailLoading,
  } = useSalesStatisticsStore();

  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [dailyPage, setDailyPage] = useState(0);
  const [dailyPageSize, setDailyPageSize] = useState(20);

  const memoizedDates = useMemo(
    () => ({
      todayDate: formatDate(new Date())['yyyy-mm-dd'],
      thirtyDaysAgoDate: formatDate(
        (() => {
          const date = new Date();
          date.setDate(date.getDate() - 30);
          return date;
        })(),
      )['yyyy-mm-dd'],
    }),
    [],
  );

  // 관리자인 경우 가맹점 목록 조회
  useEffect(() => {
    if (isAdmin) {
      getFranchise({ page: 0, size: 1000 });
    }
  }, [isAdmin, getFranchise]);

  const getFilteredData = useCallback(
    (params?: any) => {
      const parsedQuery = queryString.parse(location.search);
      const franchiseId =
        params?.franchiseId !== undefined
          ? params.franchiseId
          : (parsedQuery.franchiseId as string) || '';
      const startDt =
        params?.startDt ||
        (parsedQuery.startDt as string) ||
        memoizedDates.thirtyDaysAgoDate;
      const endDt =
        params?.endDt ||
        (parsedQuery.endDt as string) ||
        memoizedDates.todayDate;

      const queryParams: Record<string, string> = {
        startDt,
        endDt,
      };
      if (franchiseId) {
        queryParams.franchiseId = franchiseId;
      }

      updateUrlWithParams(queryParams, navigate);
    },
    [location.search, navigate, memoizedDates],
  );

  // 가맹점 필터 변경 시 URL 업데이트
  const handleFranchiseChange = useCallback(
    (franchiseId: string) => {
      const parsedQuery = queryString.parse(location.search);
      const startDt =
        (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
      const endDt =
        (parsedQuery.endDt as string) || memoizedDates.todayDate;

      const queryParams: Record<string, string> = {
        startDt,
        endDt,
      };
      if (franchiseId) {
        queryParams.franchiseId = franchiseId;
      }

      updateUrlWithParams(queryParams, navigate);
    },
    [location.search, navigate, memoizedDates],
  );

  // URL 변경 감지하여 데이터 로드
  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const startDt =
      (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
    const endDt = (parsedQuery.endDt as string) || memoizedDates.todayDate;
    const franchiseId = (parsedQuery.franchiseId as string) || '';

    getSalesStatistics({ startDt, endDt, franchiseId });
    getDailySales({ startDt, endDt, franchiseId, page: 0, size: DEFAULT_DAILY_PAGE_SIZE });
    setDailyPage(0);
    setDailyPageSize(DEFAULT_DAILY_PAGE_SIZE);
    setExpandedDates(new Set());
  }, [location.search, getSalesStatistics, getDailySales, memoizedDates]);

  // 기간 탭 선택 시 URL 파라미터 변경 → API 재호출
  const handlePeriodChange = useCallback(
    (period: PeriodType) => {
      setSelectedPeriod(period);

      const today = new Date();
      const start = getStartDateByPeriod(period);

      const parsedQuery = queryString.parse(location.search);
      const queryParams: Record<string, string> = {
        startDt: formatDate(start)['yyyy-mm-dd'],
        endDt: formatDate(today)['yyyy-mm-dd'],
      };
      if (parsedQuery.franchiseId) {
        queryParams.franchiseId = parsedQuery.franchiseId as string;
      }

      updateUrlWithParams(queryParams, navigate);
    },
    [navigate, location.search],
  );

  // franchiseSummary를 금액 내림차순으로 정렬
  const franchiseRanking = useMemo(() => {
    return [...franchiseSummary].sort((a, b) => b.totalOrderAmount - a.totalOrderAmount);
  }, [franchiseSummary]);

  // 페이지네이션 적용된 순위 데이터
  const totalRankingPages = Math.ceil(franchiseRanking.length / rankingPageSize);
  const pagedRanking = useMemo(() => {
    const start = rankingPage * rankingPageSize;
    return franchiseRanking.slice(start, start + rankingPageSize);
  }, [franchiseRanking, rankingPage, rankingPageSize]);

  // 페이지 번호 목록 (최대 5개)
  const rankingPageNumbers = useMemo(() => {
    const maxVisible = 5;
    let startPage = Math.max(0, rankingPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalRankingPages, startPage + maxVisible);
    if (endPage - startPage < maxVisible) {
      startPage = Math.max(0, endPage - maxVisible);
    }
    return Array.from({ length: endPage - startPage }, (_, i) => startPage + i);
  }, [rankingPage, totalRankingPages]);

  const handleRankingPageSizeChange = useCallback((size: number) => {
    setRankingPageSize(size);
    setRankingPage(0);
  }, []);

  // 일자별 판매량 - 아코디언 토글
  const toggleDateExpansion = useCallback(
    (e: React.MouseEvent, date: string) => {
      e.preventDefault();
      e.stopPropagation();

      const parsedQuery = queryString.parse(location.search);
      const startDt =
        (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
      const endDt = (parsedQuery.endDt as string) || memoizedDates.todayDate;
      const franchiseId = (parsedQuery.franchiseId as string) || '';

      setExpandedDates(prev => {
        const newSet = new Set(prev);
        if (newSet.has(date)) {
          newSet.delete(date);
        } else {
          newSet.add(date);
          getDailySalesDetail({ startDt, endDt, date, franchiseId });
        }
        return newSet;
      });
    },
    [location.search, memoizedDates, getDailySalesDetail],
  );

  const toggleAllDailyExpansion = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (expandedDates.size === dailySales.length) {
        setExpandedDates(new Set());
      } else {
        const parsedQuery = queryString.parse(location.search);
        const startDt =
          (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
        const endDt = (parsedQuery.endDt as string) || memoizedDates.todayDate;
        const franchiseId = (parsedQuery.franchiseId as string) || '';

        const allDates = dailySales.map(s => s.createdAt);
        allDates.forEach(date => {
          if (!dailySalesDetails[date]) {
            getDailySalesDetail({ startDt, endDt, date, franchiseId });
          }
        });
        setExpandedDates(new Set(allDates));
      }
    },
    [expandedDates.size, dailySales, dailySalesDetails, location.search, memoizedDates, getDailySalesDetail],
  );

  // 일자별 판매량 - 페이지 변경
  const handleDailyPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const startDt =
        (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
      const endDt = (parsedQuery.endDt as string) || memoizedDates.todayDate;
      const franchiseId = (parsedQuery.franchiseId as string) || '';

      setDailyPage(page);
      setExpandedDates(new Set());
      getDailySales({ startDt, endDt, franchiseId, page, size: dailyPageSize });
    },
    [location.search, memoizedDates, getDailySales, dailyPageSize],
  );

  const handleDailyPageSizeChange = useCallback(
    (size: number) => {
      const parsedQuery = queryString.parse(location.search);
      const startDt =
        (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
      const endDt = (parsedQuery.endDt as string) || memoizedDates.todayDate;
      const franchiseId = (parsedQuery.franchiseId as string) || '';

      setDailyPageSize(size);
      setDailyPage(0);
      setExpandedDates(new Set());
      getDailySales({ startDt, endDt, franchiseId, page: 0, size });
    },
    [location.search, memoizedDates, getDailySales],
  );

  // 일자별 판매량 - 페이지 번호 목록
  const dailyTotalPages = dailySalesPage?.totalPages || 0;
  const dailyPageNumbers = useMemo(() => {
    const maxVisible = 5;
    let startPage = Math.max(0, dailyPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(dailyTotalPages, startPage + maxVisible);
    if (endPage - startPage < maxVisible) {
      startPage = Math.max(0, endPage - maxVisible);
    }
    return Array.from({ length: endPage - startPage }, (_, i) => startPage + i);
  }, [dailyPage, dailyTotalPages]);

  // 일자별 판매량 - 합계 (서버 summary 사용)
  const dailySummary = useMemo(() => {
    if (dailySalesTotalSummary) {
      return {
        totalFranchiseCount: dailySalesTotalSummary.totalFranchiseCount,
        totalOrderCount: dailySalesTotalSummary.totalOrderCount,
        totalOrderAmount: dailySalesTotalSummary.totalOrderAmount,
      };
    }
    return { totalFranchiseCount: 0, totalOrderCount: 0, totalOrderAmount: 0 };
  }, [dailySalesTotalSummary]);

  // 요약 통계 (franchiseSummary에서 계산)
  const summary = useMemo(() => {
    const totalOrderCount = franchiseSummary.reduce(
      (sum, item) => sum + (item.totalOrderCount || 0), 0
    );
    const confirmedCount = franchiseSummary.reduce(
      (sum, item) => sum + (item.confirmedCount || 0), 0
    );
    const totalOrderAmount = franchiseSummary.reduce(
      (sum, item) => sum + (item.totalOrderAmount || 0), 0
    );
    const averageOrderAmount = confirmedCount > 0
      ? Math.round(totalOrderAmount / confirmedCount)
      : 0;
    return { totalOrderCount, confirmedCount, totalOrderAmount, averageOrderAmount };
  }, [franchiseSummary]);

  // 엑셀 다운로드
  const [excelDownloading, setExcelDownloading] = useState(false);

  const handleExcelDownload = useCallback(async () => {
    if (franchiseRanking.length === 0 && dailySales.length === 0) {
      toast.warn('다운로드할 데이터가 없습니다.');
      return;
    }

    const parsedQuery = queryString.parse(location.search);
    const startDt = (parsedQuery.startDt as string) || memoizedDates.thirtyDaysAgoDate;
    const endDt = (parsedQuery.endDt as string) || memoizedDates.todayDate;
    const franchiseId = (parsedQuery.franchiseId as string) || '';

    setExcelDownloading(true);

    try {
      // 모든 날짜의 상세 데이터를 병렬로 조회
      const allDetails: Record<string, IDailySalesDetail[]> = {};
      await Promise.all(
        dailySales.map(async (stat) => {
          try {
            const response = await apiClient.get<{ page: { content: IDailySalesDetail[] } }>(
              '/contract-statistics/franchise/daily',
              {
                params: {
                  startDt,
                  endDt,
                  date: stat.createdAt,
                  franchiseId: franchiseId || undefined,
                  page: 0,
                  size: 1000,
                },
              }
            );
            allDetails[stat.createdAt] = response.data.page.content || [];
          } catch {
            allDetails[stat.createdAt] = [];
          }
        })
      );

      const wb = XLSX.utils.book_new();

      // --- 시트1: 요약 + 가맹점별 판매 순위 ---
      const summaryHeader = [
        ['조회기간', `${startDt} ~ ${endDt}`],
        [],
        ['총 주문건수', summary.totalOrderCount],
        ['확정건수', summary.confirmedCount],
        ['총 매출금액', summary.totalOrderAmount],
        ['건당 평균금액', summary.averageOrderAmount],
        [],
        ['[가맹점별 판매 순위]'],
        ['순위', '가맹점 ID', '가맹점명', '총 주문건수', '확정건수', '총 매출금액', '건당 평균금액'],
      ];

      const rankingRows = franchiseRanking.map((item, index) => [
        index + 1,
        item.franchiseId,
        item.companyName,
        item.totalOrderCount || 0,
        item.confirmedCount || 0,
        item.totalOrderAmount || 0,
        item.averageOrderAmount || 0,
      ]);

      const rankingFooter = [
        ['', '', '합계', summary.totalOrderCount, summary.confirmedCount, summary.totalOrderAmount, summary.averageOrderAmount],
      ];

      const ws1Data = [...summaryHeader, ...rankingRows, ...rankingFooter];
      const ws1 = XLSX.utils.aoa_to_sheet(ws1Data);
      ws1['!cols'] = [
        { wch: 8 }, { wch: 38 }, { wch: 20 }, { wch: 14 },
        { wch: 12 }, { wch: 16 }, { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(wb, ws1, '가맹점별 판매 순위');

      // --- 시트2: 일자별 판매량 통계 ---
      const dailyHeader = [
        ['조회기간', `${startDt} ~ ${endDt}`],
        [],
        ['[일자별 판매량 통계]'],
        ['날짜', '가맹점 ID', '가맹점명', '가맹점 수', '주문건수', '판매금액'],
      ];

      const dailyRows: any[][] = [];
      dailySales.forEach(stat => {
        dailyRows.push([
          stat.createdAt,
          '',
          '',
          stat.amount.FRANCHISE_COUNT || 0,
          stat.amount.ORDER_COUNT || 0,
          stat.amount.ORDER_AMOUNT || 0,
        ]);
        const details = allDetails[stat.createdAt];
        if (details && details.length > 0) {
          details.forEach(detail => {
            dailyRows.push([
              '',
              detail.franchiseId || '',
              detail.companyName,
              '',
              detail.amount.ORDER_COUNT || 0,
              detail.amount.ORDER_AMOUNT || 0,
            ]);
          });
        }
      });

      const dailyFooter = dailySalesTotalSummary
        ? [['합계', '', '', dailySalesTotalSummary.totalFranchiseCount, dailySalesTotalSummary.totalOrderCount, dailySalesTotalSummary.totalOrderAmount]]
        : [];

      const ws2Data = [...dailyHeader, ...dailyRows, ...dailyFooter];
      const ws2 = XLSX.utils.aoa_to_sheet(ws2Data);
      ws2['!cols'] = [
        { wch: 14 }, { wch: 38 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(wb, ws2, '일자별 판매량 통계');

      XLSX.writeFile(wb, `가맹점별_판매통계_${startDt}_${endDt}.xlsx`);
      toast.success('엑셀 다운로드가 완료되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      toast.error('엑셀 다운로드에 실패했습니다.');
    }

    setExcelDownloading(false);
  }, [franchiseRanking, summary, dailySales, dailySalesTotalSummary, location.search, memoizedDates]);

  return (
    <div className="!flex !flex-col !h-full !gap-6 !p-6">
      {/* 필터 영역 */}
      <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-4">
        <SalesStatisticsFilter
          getFilteredData={getFilteredData}
          onFranchiseChange={handleFranchiseChange}
          onExcelDownload={handleExcelDownload}
          excelDownloading={excelDownloading}
        />
      </div>

      {/* 차트 영역 */}
      {getSalesStatisticsLoading ? (
        <div className="!flex-1 !grid !grid-cols-1 lg:!grid-cols-2 !gap-6">
          <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6 !flex !justify-center !items-center !min-h-[484px]">
            <p className="!text-gray-500">차트 로딩중...</p>
          </div>
          <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6 !flex !justify-center !items-center !min-h-[484px]">
            <p className="!text-gray-500">차트 로딩중...</p>
          </div>
        </div>
      ) : (
        <>

          {/* 판매 통계 테이블 (탭 전환) */}
          <div className="!bg-white !rounded-lg !shadow-sm !border !border-gray-200 !p-6">
            <div className="!flex !gap-2 !mb-4">
              {[
                { key: 'ranking' as const, label: '가맹점별 판매 순위' },
                { key: 'daily' as const, label: '일자별 판매량 통계' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={e => {
                    e.preventDefault();
                    setActiveTable(key);
                  }}
                  className={`!px-4 !py-2 !rounded-lg !text-sm !font-medium !transition-colors ${
                    activeTable === key
                      ? '!bg-blue-500 !text-white'
                      : '!bg-gray-100 !text-gray-700 hover:!bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTable === 'ranking' ? (
              <FranchiseRankingTable
                franchiseRanking={franchiseRanking}
                pagedRanking={pagedRanking}
                summary={summary}
                rankingPage={rankingPage}
                rankingPageSize={rankingPageSize}
                totalRankingPages={totalRankingPages}
                rankingPageNumbers={rankingPageNumbers}
                setRankingPage={setRankingPage}
                handleRankingPageSizeChange={handleRankingPageSizeChange}
              />
            ) : (
              <DailySalesTable
                dailySales={dailySales}
                dailySalesLoading={dailySalesLoading}
                dailySalesPage={dailySalesPage}
                dailySalesDetails={dailySalesDetails}
                dailySalesDetailLoading={dailySalesDetailLoading}
                dailySummary={dailySummary}
                expandedDates={expandedDates}
                toggleDateExpansion={toggleDateExpansion}
                toggleAllDailyExpansion={toggleAllDailyExpansion}
                dailyPage={dailyPage}
                dailyTotalPages={dailyTotalPages}
                dailyPageNumbers={dailyPageNumbers}
                handleDailyPageChange={handleDailyPageChange}
                handleDailyPageSizeChange={handleDailyPageSizeChange}
                dailyPageSize={dailyPageSize}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Statistics;
